import { PORTAL_CLASS_NAME } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useKey } from 'react-use'

import { SandboxModeBanner } from '../SandboxModeBanner'
import * as Styled from './styles'

interface BankTransactionsSandboxPaneProps {
  /**
   * Render prop instead of a plain node so callers can portal a Drawer into `cardElement`
   * (see PaneCard's `transform` in styles.ts) once it's mounted, keeping it confined to this
   * card instead of the full viewport.
   */
  children: (cardElement: HTMLDivElement | null) => ReactNode
  onClose: () => void
  onReset: () => void
}

export const BankTransactionsSandboxPane = ({
  children,
  onClose,
  onReset,
}: BankTransactionsSandboxPaneProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null)

  const handleEscapePress = useCallback(() => {
    const hasOpenOverlay = document.querySelector(`.${PORTAL_CLASS_NAME}`) !== null

    if (!hasOpenOverlay) {
      onClose()
    }
  }, [onClose])

  useKey('Escape', handleEscapePress)

  return ReactDOM.createPortal(
    <Styled.PaneWrapper ref={wrapperRef} role="dialog" aria-modal="true">
      <Styled.PaneCard ref={setCardElement}>
        <SandboxModeBanner onClose={onClose} onReset={onReset} />
        <Styled.PaneContent>{children(cardElement)}</Styled.PaneContent>
      </Styled.PaneCard>
    </Styled.PaneWrapper>,
    document.body,
  )
}
